import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import AssignmentTwoToneIcon from "@mui/icons-material/AssignmentTwoTone";
import EventAvailableTwoToneIcon from "@mui/icons-material/EventAvailableTwoTone";
import PendingActionsTwoToneIcon from "@mui/icons-material/PendingActionsTwoTone";
import CategoryTwoToneIcon from "@mui/icons-material/CategoryTwoTone";
import TrendingDownTwoToneIcon from "@mui/icons-material/TrendingDownTwoTone";
import ChangeCircleTwoToneIcon from "@mui/icons-material/ChangeCircleTwoTone";
import { ArrowForward, LocationOn } from "@mui/icons-material";
import { instance } from "../Components/config";
import { DialogAlert } from "../Components/DialogAlert";
import { useAuth } from "../Components/AuthContext";
import { useNavigate } from "react-router-dom";
import { LoaderBox } from "../Components/LoaderBox";

const TIPOS = ["personas", "eventos", "documentos", "semillas"];

function StatCard({ icon, titulo, children }) {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>{titulo}</Typography>
      </Box>
      {children}
    </Paper>
  );
}

function Homepage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialog, setDialog] = useState({ title: "", text: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [avance, setAvance] = useState([]);
  const [pendientesRevision, setPendientesRevision] = useState(0);
  const [resumenUpse8, setResumenUpse8] = useState([]);
  const [pendientesRevisionUpse8, setPendientesRevisionUpse8] = useState(0);

  useEffect(() => {
    Promise.all([
      instance.get("/dashboard/avanceGlobal"),
      instance.get(`/dashboard/pendientesRevision?id=${user.id}`),
      instance.get("/dashboard/resumenUPSE8"),
      instance.get(`/dashboard/pendientesRevisionUPSE8?id=${user.id}`),
    ])
      .then(([resAvance, resPendientes, resUpse8, resPendientesUpse8]) => {
        setAvance(resAvance.data);
        setPendientesRevision(resPendientes.data.total);
        setResumenUpse8(resUpse8.data);
        setPendientesRevisionUpse8(resPendientesUpse8.data.total);
      })
      .catch((err) => {
        setDialog({
          title: "ERROR",
          text: err.response?.status === 500
            ? "Existe algún problema con el servidor, por favor inténtalo de nuevo mas tarde."
            : err.response?.data?.message || "Error desconocido",
        });
        setOpenDialog(true);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alDia = (item) => item.mesesEsperados === 0 || item.mesesReportados >= item.mesesEsperados;

  const totalUpse2 = avance.length;
  const porEstado = useMemo(() => {
    const acc = { Aprobado: 0, "Pendiente de revisión": 0, Rechazado: 0 };
    avance.forEach((item) => { acc[item.estado] = (acc[item.estado] || 0) + 1; });
    return acc;
  }, [avance]);

  const cantAlDia = useMemo(() => avance.filter(alDia).length, [avance]);
  const cantAtrasados = totalUpse2 - cantAlDia;

  const totalUpse8 = resumenUpse8.length;
  const porEstadoUpse8 = useMemo(() => {
    const acc = { Aprobado: 0, "Pendiente de revisión": 0, Rechazado: 0 };
    resumenUpse8.forEach((item) => { acc[item.estado] = (acc[item.estado] || 0) + 1; });
    return acc;
  }, [resumenUpse8]);

  const porTipo = useMemo(() => {
    const acc = { personas: 0, eventos: 0, documentos: 0, semillas: 0 };
    avance.forEach((item) => { acc[item.tipoPlanificacion] = (acc[item.tipoPlanificacion] || 0) + 1; });
    return acc;
  }, [avance]);

  const topAtrasados = useMemo(() => {
    return avance
      .filter((item) => !alDia(item))
      .map((item) => ({ ...item, brecha: item.mesesEsperados - item.mesesReportados }))
      .sort((a, b) => b.brecha - a.brecha)
      .slice(0, 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avance]);

  const irAMesesUpse2 = (item) => {
    navigate("/UPSE4MesesPorUpse2", {
      state: {
        idUpse2: item.idUpse2,
        nombreUpse2: item.direccion,
        anio: item.anio,
        tipoPlanificacion: item.tipoPlanificacion,
      },
    });
  };

  return (
    <LoaderBox isLoading={isLoading}>
      <Container maxWidth="xl" sx={{ marginTop: 2, marginBottom: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Panel general</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<AssignmentTwoToneIcon color="primary" />} titulo="UPSE-2 creados">
              <Typography variant="h3" fontWeight={700}>{totalUpse2}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Aprobados {porEstado["Aprobado"]} · Pendientes {porEstado["Pendiente de revisión"]} · Rechazados {porEstado["Rechazado"]}
              </Typography>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<EventAvailableTwoToneIcon color="primary" />} titulo="Al día vs. atrasados">
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box>
                  <Typography variant="h3" fontWeight={700} color="success.main">{cantAlDia}</Typography>
                  <Typography variant="body2" color="text.secondary">Al día</Typography>
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight={700} color="warning.main">{cantAtrasados}</Typography>
                  <Typography variant="body2" color="text.secondary">Atrasados</Typography>
                </Box>
              </Box>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<PendingActionsTwoToneIcon color="primary" />} titulo="Pendientes de mi revisión">
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box>
                  <Typography variant="h3" fontWeight={700}>{pendientesRevision}</Typography>
                  <Typography variant="body2" color="text.secondary">UPSE-4</Typography>
                  <Button size="small" sx={{ mt: 1 }} variant="outlined" onClick={() => navigate("/UPSE4Pendientes")}>
                    Ir a revisar
                  </Button>
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight={700}>{pendientesRevisionUpse8}</Typography>
                  <Typography variant="body2" color="text.secondary">UPSE-8</Typography>
                  <Button size="small" sx={{ mt: 1 }} variant="outlined" onClick={() => navigate("/UPSE8")}>
                    Ir a revisar
                  </Button>
                </Box>
              </Box>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<CategoryTwoToneIcon color="primary" />} titulo="Por tipo de planificación">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {TIPOS.map((tipo) => (
                  <Box key={tipo} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ textTransform: "capitalize" }}>{tipo}</Typography>
                    <Typography variant="body2" fontWeight={600}>{porTipo[tipo]}</Typography>
                  </Box>
                ))}
              </Box>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<ChangeCircleTwoToneIcon color="primary" />} titulo="Reprogramaciones UPSE-8">
              <Typography variant="h3" fontWeight={700}>{totalUpse8}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Aprobadas {porEstadoUpse8["Aprobado"]} · Pendientes {porEstadoUpse8["Pendiente de revisión"]} · Rechazadas {porEstadoUpse8["Rechazado"]}
              </Typography>
            </StatCard>
          </Grid>

          <Grid item xs={12}>
            <StatCard icon={<TrendingDownTwoToneIcon color="primary" />} titulo="UPSE-2 más atrasados">
              {topAtrasados.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Todo al día — no hay UPSE-2 atrasados.</Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {topAtrasados.map((item) => (
                    <Paper
                      key={item.idUpse2}
                      variant="outlined"
                      onClick={() => irAMesesUpse2(item)}
                      sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        "&:hover": { borderColor: "#bbb" },
                      }}
                    >
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip label={`U2-${item.idUpse2}`} size="small" variant="outlined" color="primary" />
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="body1" fontWeight={600}>{item.localidad}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {item.anio} · {item.mesesReportados}/{item.mesesEsperados} meses reportados ·{" "}
                          <Chip label={item.tipoPlanificacion} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} />
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip label={`-${item.brecha} mes(es)`} size="small" color="warning" />
                        <ArrowForward fontSize="small" color="primary" />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </StatCard>
          </Grid>
        </Grid>

        <DialogAlert
          title={dialog.title}
          text={dialog.text}
          open={openDialog}
          setOpen={setOpenDialog}
        />
      </Container>
    </LoaderBox>
  );
}

export { Homepage };
